"use client";

import { DeviceType, Language } from "@finbackoffice/enums";
import {
    AffiliateTrackerProvider,
    AuthProvider,
    BrowserStorageContext,
    BrowserStorageProvider,
    CasinoFavoritesProvider,
    ChatProvider,
    ChatServiceProvider,
    ClientProvider,
    SportFavoritesProvider,
    FeedProvider,
    FeedRidContext,
    LiveFeedContext,
    PrematchFeedContext,
    RuntimeConfigProvider,
} from "@finbackoffice/site-core";
import { FC, PropsWithChildren, useContext } from "react";
import { IRuntimeConfig } from "@finbackoffice/fe-core";
import {
    ICasinoFavoriteResponse,
    IProfileResponse,
    ISiteConfigs,
} from "@finbackoffice/clientbff-client";
import {
    BetSlipProviderV2,
    CasinoToolsProvider,
    ConfigProviderV2,
    CrmProvider,
    ExchangeRatesProviderV2,
    I18nextProviderV2,
    UIStateProvider,
    MarketUpdatesProvider,
    ModalsProviderV2,
    NotificationProvider,
    PrematchProvider,
    SIRWidgetProvider,
    SiteDirection,
    ActiveSportGameProvider,
    SportResultsProvider,
    UserAccountProviderV2,
    WalletsProvider,
    WebsocketProvider,
} from "contexts";
import ChatScript from "components/base/external-scripts/ChatScript";
import GlobalActionsContainer from "components/globals/GlobalActionsContainer";
import { useSocketStorage } from "hooks";

const SportFavoritesWrapper: FC<PropsWithChildren> = ({ children }) => {
    const { hasLiveFeedGames } = useContext(LiveFeedContext);
    const { hasPrematchSports } = useContext(PrematchFeedContext);
    const { liveRid } = useContext(FeedRidContext);
    const socketStorage = useSocketStorage(liveRid);
    const browserStorage = useContext(BrowserStorageContext);

    return (
        <SportFavoritesProvider
            browserStorage={browserStorage}
            socketStorage={socketStorage}
            liveFeedReady={hasLiveFeedGames}
            prematchFeedReady={hasPrematchSports}>
            {children}
        </SportFavoritesProvider>
    );
};

type IProvidersProps = {
    locale: Language;
    translations: Record<string, any>;
    uuid: string;
    appConfig: IRuntimeConfig;
    profile: IProfileResponse | null;
    casinoFavoriteGames: ICasinoFavoriteResponse[] | null;
    configs: ISiteConfigs;
    token: string;
    disableNotificationPopups: boolean;
    siteDirection: SiteDirection;
    deviceType: DeviceType;
    referralCode?: string;
};

export const Providers: FC<PropsWithChildren<IProvidersProps>> = (props) => {
    const commonSiteConfig = props.appConfig.COMMON_SITE_CONFIGS;

    return (
        <I18nextProviderV2 translations={props.translations} locale={props.locale}>
            <BrowserStorageProvider>
                <AuthProvider initialJwt={props.token}>
                    <ClientProvider>
                        <RuntimeConfigProvider config={props.appConfig}>
                            <NotificationProvider>
                                <AffiliateTrackerProvider>
                                    <WebsocketProvider
                                        locale={props.locale}
                                        clientWsEndpoint={props.appConfig.CLIENT_WS_ENDPOINT}>
                                        <ConfigProviderV2
                                            defaultConfig={props.configs}
                                            defaultCountry={commonSiteConfig.country.default}
                                            siteDirection={props.siteDirection}
                                            deviceType={props.deviceType}
                                            referralCode={props.referralCode}>
                                            <BetSlipProviderV2>
                                                <UserAccountProviderV2
                                                    initialProfile={props.profile}
                                                    uuid={props.uuid}>
                                                    <CrmProvider locale={props.locale}>
                                                        <ChatServiceProvider
                                                            script={<ChatScript />}>
                                                            <WalletsProvider>
                                                                <ModalsProviderV2>
                                                                    <ExchangeRatesProviderV2
                                                                        fxCurrency={
                                                                            commonSiteConfig.wallet
                                                                                .fxCurrency
                                                                        }>
                                                                        <FeedProvider>
                                                                            <SportFavoritesWrapper>
                                                                                <ActiveSportGameProvider>
                                                                                    <SportResultsProvider>
                                                                                        <SIRWidgetProvider>
                                                                                            <PrematchProvider>
                                                                                                <UIStateProvider>
                                                                                                    <CasinoFavoritesProvider
                                                                                                        favoriteGames={
                                                                                                            props.casinoFavoriteGames ||
                                                                                                            null
                                                                                                        }
                                                                                                        mobile={
                                                                                                            props.deviceType ===
                                                                                                            DeviceType.Mobile
                                                                                                        }>
                                                                                                        <CasinoToolsProvider>
                                                                                                            <ChatProvider>
                                                                                                                <MarketUpdatesProvider>
                                                                                                                    <GlobalActionsContainer>
                                                                                                                        {
                                                                                                                            props.children
                                                                                                                        }
                                                                                                                    </GlobalActionsContainer>
                                                                                                                </MarketUpdatesProvider>
                                                                                                            </ChatProvider>
                                                                                                        </CasinoToolsProvider>
                                                                                                    </CasinoFavoritesProvider>
                                                                                                </UIStateProvider>
                                                                                            </PrematchProvider>
                                                                                        </SIRWidgetProvider>
                                                                                    </SportResultsProvider>
                                                                                </ActiveSportGameProvider>
                                                                            </SportFavoritesWrapper>
                                                                        </FeedProvider>
                                                                    </ExchangeRatesProviderV2>
                                                                </ModalsProviderV2>
                                                            </WalletsProvider>
                                                        </ChatServiceProvider>
                                                    </CrmProvider>
                                                </UserAccountProviderV2>
                                            </BetSlipProviderV2>
                                        </ConfigProviderV2>
                                    </WebsocketProvider>
                                </AffiliateTrackerProvider>
                            </NotificationProvider>
                        </RuntimeConfigProvider>
                    </ClientProvider>
                </AuthProvider>
            </BrowserStorageProvider>
        </I18nextProviderV2>
    );
};
